import {
  faChevronCircleDown,
  faChevronCircleRight,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { flatten } from 'lodash';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import { Fragment, useState } from 'react';
import type { ButtonProps } from 'src/components/buttons-and-actions/button';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import type { EmptyCardProps } from 'src/components/data-display/empty-card';
import { EmptyCard } from 'src/components/data-display/empty-card';
import type { TrProps } from 'src/components/data-display/table';
import {
  SimpleTable,
  Td,
  Th,
  Thead,
  thStyle,
  Tr,
} from 'src/components/data-display/table';
import { useOptionalFormMode } from 'src/components/form/form-mode-context';
import type { PaginationProps } from 'src/components/navigation/pagination';
import { Pagination } from 'src/components/navigation/pagination';
import { SpinnerContainer } from 'src/components/spinner-container';
import { Hint } from 'src/components/text/hint';
import { cn } from 'src/utils/cn';

type ColActions = {
  showMeta: boolean;
  setShowMeta: (value: boolean) => void;
};

type MetaProps = {
  close: () => void;
};

export type Col<Item> = {
  key: string;
  head: JSX.Element;
  body: (item: Item, index: number, actions: ColActions) => JSX.Element;
  /**
   * Either specify a fixed width like `'5.5rem'` as a string or a relative width
   * like `1` as a number which would be treated just like `'1fr'` in a CSS grid.
   *
   * Let's say you have a table like this:
   * ```tsx
   * <Table
   *   data={[]}
   *   cols={[
   *     { width: 2 },
   *     {}, // defaults to width: 1
   *     { width: '5.5rem' },
   *   ]}
   * />
   * ```
   *
   * This means your last column has a width of 5.5rem (plus padding which will be added later)
   * and the other two columns get the remaining space will the first one will be 66.6% and the
   * second one 33.3%.
   */
  width?: string | number;
};

export type ColGroup<Item> = {
  key: string;
  group: string;
  cols: (Col<Item> | false)[];
};

type RowProps<Item extends {}> = {
  item: Item;
  trProps: TrProps;
};

type BodyProps = {
  tbodyProps: ComponentPropsWithoutRef<'tbody'>;
};

type SmartRowProps<Item extends {}> = {
  item: Item;
  meta?: (item: Item, index: number, props: MetaProps) => JSX.Element;
  filteredCols: Col<Item>[];
  rowIndex: number;
  colsCount: number;
  onClickRow?: (item: Item, index: number) => void;
  row?: (rowProps: RowProps<Item>) => ReactNode;
  light?: boolean;
};

function SmartRow<Item extends {}>({
  item,
  meta,
  filteredCols,
  rowIndex,
  colsCount,
  onClickRow,
  light,
  row,
}: SmartRowProps<Item>) {
  const [showMeta, setShowMeta] = useState(false);
  const isEven = Boolean(rowIndex % 2);

  const trProps: TrProps = {
    isEven,
    hideBorder: showMeta,
    light,
    onClick: onClickRow ? () => onClickRow(item, rowIndex) : undefined,
    children: filteredCols.map((col) => (
      <Fragment key={col.key}>
        {col.body(item, rowIndex, { showMeta, setShowMeta })}
      </Fragment>
    )),
  };

  return (
    <>
      {row ? row({ item, trProps }) : <Tr {...trProps} light={light} />}

      {showMeta && meta && (
        <tr>
          <td colSpan={colsCount}>
            <Card mode="embedded">
              {meta(item, rowIndex, { close: () => setShowMeta(false) })}
            </Card>
          </td>
        </tr>
      )}
    </>
  );
}

type SetId<Item extends { id?: string | number }> = Item['id'] extends
  | string
  | number
  ? { setId?: (item: Item, index: number) => string | number }
  : { setId: (item: Item, index: number) => string | number };

type TableProps<Item extends {}> = {
  respectHeader?: boolean;
  data: readonly Item[];
  meta?: (item: Item, index: number, props: MetaProps) => JSX.Element;
  cols: (ColGroup<Item> | Col<Item> | false | undefined)[];
  onClickHeader?: () => void;
  onClickRow?: (item: Item, index: number) => void;
  empty?: EmptyCardProps;
  'data-testid'?: string;
  pending?: boolean;
  paginated?: PaginationProps;
  hint?: ReactNode;
  row?: (rowProps: RowProps<Item>) => ReactNode;
  body?: (bodyProps: BodyProps) => ReactNode;
  mode?: 'regular' | 'light';
  staticHeader?: boolean;
  /**
   * When a table contains form fields, we top align the table cell content.
   * This is because form validation might show errors at runtime which changes
   * the cell height which causes the cell content to jump around, when it
   * is not top aligned.
   *
   * Note: This will automatically be set to `true` if the table is embedded in a form,
   * but it can be manually overridden in case you have a table embedded in a form,
   * but it doesn't contain any form fields.
   */
  containsFormFields?: boolean;
  className?: string;
} & SetId<Item>;

export function Table<Item extends {}>({
  respectHeader,
  setId,
  data,
  cols,
  onClickHeader,
  onClickRow,
  empty,
  meta,
  'data-testid': testId,
  pending = false,
  paginated,
  hint,
  body,
  row,
  mode = 'regular',
  staticHeader = false,
  containsFormFields,
  className,
}: TableProps<Item>) {
  const truthyCols = cols.filter(Boolean) as (ColGroup<Item> | Col<Item>)[];

  const flattenedCols = flatten(
    truthyCols.map((item) => {
      if ('cols' in item) {
        return item.cols.filter(Boolean) as Col<Item>[];
      } else {
        return item;
      }
    })
  );
  const widths = flattenedCols.map(({ width = 1 }) => width);

  const hasGroups = Boolean(truthyCols.filter((item) => 'cols' in item).length);

  const tbodyProps = {
    'data-testid': testId ? `${testId}-body` : undefined,
    children: data.map((item, index) => {
      // guidance while doing development
      if (process.env.NODE_ENV !== 'production') {
        if (!('id' in item) && !setId) {
          throw new Error(
            'Your table item has no "id" field. Please use <Table setId={(item) => item.someId} /> to create an id on the fly.'
          );
        }
      }
      return (
        <SmartRow
          onClickRow={onClickRow}
          light={mode === 'light'}
          key={setId ? setId(item, index) : (item as any).id}
          colsCount={flattenedCols.length}
          item={item}
          meta={meta}
          filteredCols={flattenedCols}
          rowIndex={index}
          row={row}
        />
      );
    }),
  } as ComponentPropsWithoutRef<'tbody'>;

  const embeddedInForm = Boolean(useOptionalFormMode());

  return (
    <div
      className={`
        grid gap-md
        legacy:gap-[1rem]
      `}
    >
      <div data-testid={testId}>
        <SpinnerContainer pending={pending}>
          <SimpleTable
            className={className}
            respectHeader={respectHeader}
            widths={widths}
            light={mode === 'light'}
            sticky={!staticHeader}
            containsFormFields={containsFormFields ?? embeddedInForm}
            hasPagination={Boolean(paginated)}
          >
            <Thead onClick={onClickHeader}>
              {/*
                Our tables use `table-layout: fixed;` and according to MDN "column widths
                are set by the widths of ... the first row of cells". In case of grouped column headers
                we have an irregular amount of columns and therefor the column widths would be off.
                That's why we have to use a row with empty cells, before we render the groups.
                See https://developer.mozilla.org/en-US/docs/Web/CSS/table-layout#values
              */}
              <tr aria-hidden className={cn(!hasGroups && 'hidden')}>
                {flattenedCols.map((col) => (
                  <th key={col.key} />
                ))}
              </tr>
              {hasGroups && (
                <Tr>
                  {truthyCols.map((col) =>
                    'cols' in col ? (
                      <Th
                        key={col.key}
                        colSpan={col.cols.filter(Boolean).length}
                        className={emptyColThStyles}
                      >
                        {col.group}
                      </Th>
                    ) : (
                      <th
                        key={col.key}
                        className={cn(
                          thStyle,
                          'legacy:px-[0.8rem] legacy:py-[0.4rem]'
                        )}
                      />
                    )
                  )}
                </Tr>
              )}

              <Tr>
                {flattenedCols.map((col) => (
                  <Fragment key={col.key}>{col.head}</Fragment>
                ))}
              </Tr>
            </Thead>

            {body ? body({ tbodyProps }) : <tbody {...tbodyProps} />}
          </SimpleTable>

          {data.length === 0 && empty && (
            <EmptyCard
              {...empty}
              className={cn('not-legacy:rounded-t-none', empty.className)}
              {...(testId ? { 'data-testid': `${testId}-empty-card` } : {})}
            />
          )}
        </SpinnerContainer>

        {paginated && <Pagination {...paginated} />}
      </div>

      {hint && <Hint>{hint}</Hint>}
    </div>
  );
}

type ExpandButtonProps = ButtonProps & {
  showMeta: boolean;
  setShowMeta: (showMeta: boolean) => void;
  showLabel: string;
  hideLabel: string;
};

export const ExpandButton: FC<ExpandButtonProps> = (props) => {
  const { showMeta, setShowMeta, showLabel, hideLabel, ...rest } = props;

  return (
    <Button mode="icon" onClick={() => setShowMeta(!showMeta)} {...rest}>
      {showMeta ? (
        <FontAwesomeIcon icon={faChevronCircleDown} aria-label={hideLabel} />
      ) : (
        <FontAwesomeIcon icon={faChevronCircleRight} aria-label={showLabel} />
      )}
    </Button>
  );
};

type SortableBodyPropsDual<Params extends { [key: string]: unknown }> = {
  data: [string | ReactNode, string | ReactNode];
  columns: [string, string];
  params: { value: Params; set: (value: Partial<Params>) => void };
};

export const SortableBody = (
  props: SortableBodyPropsDual<{ [key: string]: unknown }>
) => {
  let [firstLine, secondLine]: [string | ReactNode, string | ReactNode] =
    props.data;

  let sortColumn = props.params.value.sortBy as string;

  if (sortColumn === props.columns[1]) {
    [firstLine, secondLine] = [secondLine, firstLine];
  }

  return (
    <Td>
      {firstLine}
      <br />
      <small>{secondLine}</small>
    </Td>
  );
};

const emptyColThStyles = cn(`
  legacy:border-b-1 legacy:border-l-0 legacy:border-b-legacy-background
  legacy:px-[0.8rem] legacy:py-[0.4rem]
`);
